import type {
  AddMessageToTicketRequest,
  CreateTicketRequest,
  SendTicketToRichardResponse,
  Ticket,
  TicketMotive,
  TicketStatusFilter,
  TicketUrgency,
  UpdateTicketMessageRequest,
  UpdateTicketRequest,
} from 'services/types/ticket';

import baseApi from './baseApi';

export const ticketsApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getTickets: build.query<
      { tickets: Ticket[]; total: number },
      {
        includeAll?: boolean;
        page?: number;
        pageSize?: number;
        statuses?: TicketStatusFilter[];
        businessUnitIds?: number[];
        createdByUserIds?: number[];
        interestedUserIds?: number[];
        category?: 'all' | 'withBusinessUnit' | 'rollbar' | 'other';
        urgencies?: TicketUrgency[];
        motives?: TicketMotive[];
        deadlineFrom?: string;
        deadlineTo?: string;
        createdFrom?: string;
        createdTo?: string;
      }
    >({
      query: (params) => ({
        url: `/tickets?${new URLSearchParams({
          ...(params.includeAll ? { includeAll: 'true' } : {}),
          ...(params.page ? { page: params.page.toString() } : {}),
          ...(params.pageSize ? { pageSize: params.pageSize.toString() } : {}),
          ...(params.statuses && params.statuses.length > 0 ? { statuses: params.statuses.join(',') } : {}),
          ...(params.businessUnitIds && params.businessUnitIds.length > 0
            ? { businessUnitIds: params.businessUnitIds.join(',') }
            : {}),
          ...(params.createdByUserIds && params.createdByUserIds.length > 0
            ? { createdByUserIds: params.createdByUserIds.join(',') }
            : {}),
          ...(params.interestedUserIds && params.interestedUserIds.length > 0
            ? { interestedUserIds: params.interestedUserIds.join(',') }
            : {}),
          ...(params.category && params.category !== 'all' ? { category: params.category } : {}),
          ...(params.urgencies && params.urgencies.length > 0 ? { urgencies: params.urgencies.join(',') } : {}),
          ...(params.motives && params.motives.length > 0 ? { motives: params.motives.join(',') } : {}),
          ...(params.deadlineFrom ? { deadlineFrom: params.deadlineFrom } : {}),
          ...(params.deadlineTo ? { deadlineTo: params.deadlineTo } : {}),
          ...(params.createdFrom ? { createdFrom: params.createdFrom } : {}),
          ...(params.createdTo ? { createdTo: params.createdTo } : {}),
        }).toString()}`,
        method: 'GET',
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.tickets.map((t) => ({ type: 'Ticket' as const, id: t.id })),
              { type: 'Tickets' as const, id: 'LIST' },
            ]
          : [{ type: 'Tickets' as const, id: 'LIST' }],
    }),
    getTicketFilterUsers: build.query<
      {
        creators: { id: number; name: string; isAdmin: boolean }[];
        interested: { id: number; name: string; isAdmin: boolean }[];
      },
      void
    >({
      query: () => ({
        url: '/tickets/filter-interested-users',
        method: 'GET',
      }),
      providesTags: [{ type: 'Tickets' as const, id: 'FILTER_USERS' }],
    }),
    getTicket: build.query<Ticket, number>({
      query: (ticketId) => ({
        url: `/tickets/${ticketId}`,
        method: 'GET',
      }),
      providesTags: (_result, _err, ticketId) => [{ type: 'Ticket' as const, id: ticketId }],
    }),
    createTicket: build.mutation<Ticket, CreateTicketRequest>({
      query: (ticketData) => ({
        url: '/tickets',
        method: 'POST',
        body: ticketData,
      }),
      invalidatesTags: [{ type: 'Tickets' as const, id: 'LIST' }],
    }),
    addMessageToTicket: build.mutation<Ticket, { ticketId: number; data: AddMessageToTicketRequest }>({
      query: ({ ticketId, data }) => ({
        url: `/tickets/${ticketId}/messages`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (_result, _err, { ticketId }) => [
        { type: 'Ticket' as const, id: ticketId },
        { type: 'Tickets' as const, id: 'LIST' },
      ],
    }),
    uploadFileToTicket: build.mutation<
      { message: string; url: string; fileName: string },
      { ticketId: number; file: File; showToUser?: boolean }
    >({
      query: ({ ticketId, file, showToUser }) => {
        const formData = new FormData();
        formData.append('file', file);
        if (showToUser !== undefined) {
          formData.append('showToUser', showToUser.toString());
        }

        return {
          url: `/tickets/${ticketId}/upload`,
          method: 'POST',
          body: formData,
        };
      },
      transformResponse: (response: { message: string; url: string; fileName: string }) => response,
      invalidatesTags: (_result, _err, { ticketId }) => [
        { type: 'Ticket' as const, id: ticketId },
        { type: 'Tickets' as const, id: 'LIST' },
      ],
    }),
    analyzeTicket: build.mutation<Ticket, number>({
      query: (ticketId) => ({
        url: `/tickets/${ticketId}/analyze`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _err, ticketId) => [
        { type: 'Ticket' as const, id: ticketId },
        { type: 'Tickets' as const, id: 'LIST' },
      ],
    }),
    sendTicketToRichard: build.mutation<SendTicketToRichardResponse, number>({
      query: (ticketId) => ({
        url: `/tickets/${ticketId}/send-to-richard`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _err, ticketId) => [{ type: 'Ticket' as const, id: ticketId }],
    }),
    getTicketCost: build.query<{ cost: number }, number>({
      query: (ticketId) => ({
        url: `/tickets/${ticketId}/cost`,
        method: 'GET',
      }),
    }),
    updateTicket: build.mutation<Ticket, { ticketId: number; data: UpdateTicketRequest }>({
      query: ({ ticketId, data }) => ({
        url: `/tickets/${ticketId}`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (_result, _err, { ticketId }) => [
        { type: 'Ticket' as const, id: ticketId },
        { type: 'Tickets' as const, id: 'LIST' },
      ],
    }),
    transitionTicket: build.mutation<Ticket, { ticketId: number; data: { event: string } }>({
      query: ({ ticketId, data }) => ({
        url: `/tickets/${ticketId}/transition`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (_result, _err, { ticketId }) => [
        { type: 'Ticket' as const, id: ticketId },
        { type: 'Tickets' as const, id: 'LIST' },
      ],
    }),
    updateTicketMessage: build.mutation<
      { message: string },
      { ticketId: number; messageIndex: number; data: UpdateTicketMessageRequest }
    >({
      query: ({ ticketId, messageIndex, data }) => ({
        url: `/tickets/${ticketId}/messages/${messageIndex}`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (_result, _err, { ticketId }) => [
        { type: 'Ticket' as const, id: ticketId },
        { type: 'Tickets' as const, id: 'LIST' },
      ],
    }),
    updateTicketInterestedUsers: build.mutation<
      { interestedUserIds: number[] },
      { ticketId: number; userIds: number[] }
    >({
      query: ({ ticketId, userIds }) => ({
        url: `/tickets/${ticketId}/interested-users`,
        method: 'PUT',
        body: { userIds },
      }),
      invalidatesTags: (_result, _err, { ticketId }) => [{ type: 'Ticket' as const, id: ticketId }],
    }),
  }),
});

export const {
  useGetTicketsQuery,
  useGetTicketFilterUsersQuery,
  useGetTicketQuery,
  useCreateTicketMutation,
  useAddMessageToTicketMutation,
  useUploadFileToTicketMutation,
  useAnalyzeTicketMutation,
  useSendTicketToRichardMutation,
  useGetTicketCostQuery,
  useUpdateTicketMutation,
  useTransitionTicketMutation,
  useUpdateTicketMessageMutation,
  useUpdateTicketInterestedUsersMutation,
} = ticketsApi;
